import {
  ButtonVariant,
  BUTTON,
  BUTTON_STYLE,
  ICON,
  ICON_BUTTON_STYLE,
  LINK,
  MODAL_FOOTER,
  MODAL_FOOTER_STYLE,
  TINY,
  TINY_BUTTON_STYLE,
} from 'app/components/forms/Button';
import { tFunctionType, useI18n } from 'app/components/localization';
import Message from 'app/components/modals/Message';
import { getLinkStyles } from 'app/components/presentation/link/styles';
import Spinner from 'app/components/svgs/Spinner';
import { useFocusOutlineState } from 'app/containers/state/FocusOutlineState';
import { createMimeFinder, MediaType, MEDIA_TYPES } from 'app/constants/MediaTypes';
import { notify } from 'app/services/errorReporter';
import { IStyles, useClasses } from 'app/utils/css';
import { readFileDataURL as readFileDataURLImpl } from 'app/utils/env';
import { formatGenericError } from 'app/utils/errors';
import { useIsMountedRef } from 'app/utils/react';
import userAgent, { OperatingSystem } from 'app/utils/userAgent';
import React, { FunctionComponent, ReactNode, useRef, useState } from 'react';
import filesize from 'file-size';
import { List } from 'immutable';
import keycode from 'keycode';
import { AlertVariant } from 'app/components/presentation/alert/AlertBox';

// Default to 15MB to be able to handle some 24MP cameras
export const SIZE_LIMIT = 15 * 1000 * 1000;

export const SPACE_KEYCODE = keycode('space');
export const ENTER_KEYCODE = keycode('enter');

type FileButtonVariantSubset =
  ButtonVariant.Link |
  ButtonVariant.Button |
  ButtonVariant.Tiny |
  ButtonVariant.Icon |
  ButtonVariant.ModalFooter;

interface IProps {
  children?: ReactNode;
  asBlob?: boolean;
  disabled?: boolean;
  maxSize?: number;
  showErrorModal?: boolean;
  types: List<MediaType>;
  variant?: FileButtonVariantSubset;
  onError?: (error: Error) => void;
  onNewFile: (payload: any) => void;
  validator?: (payload: any) => Promise<any>;
  'aria-describedby'?: string,
  readFileDataURL?: (file: Blob) => Promise<string>,
}

export const FileInput: FunctionComponent<IProps> = ({
  children,
  asBlob = true,
  disabled,
  maxSize = SIZE_LIMIT,
  showErrorModal = true,
  types = MEDIA_TYPES,
  variant = LINK,
  onError = () => {},
  onNewFile,
  validator = () => Promise.resolve(),
  'aria-describedby': ariaDescribedBy,
  readFileDataURL = readFileDataURLImpl,
}: IProps) => {
  const { t } = useI18n();
  const isMounted = useIsMountedRef();
  const input = useRef<HTMLInputElement | null>(null);
  const [currentError, setCurrentError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const isDisabledOrLoading = isLoading || disabled;
  const [showOutline, focusableRef] = useFocusOutlineState();
  const classes = useClasses({
    inputWrap: {
      position: 'absolute',
      width: '0',
      height: '0',
      overflow: 'hidden',
    },
    transparentChildren: {
      visibility: 'hidden',
    },
    spinner: {
      position: 'absolute',
      top: '0',
      left: '0',
      right: '0',
      bottom: '0',
      textAlign: 'center',
    },
    label: [
      {
        position: 'relative',
        ...variantToStyles[variant],
      },
      isDisabledOrLoading && {
        ...variantToStyles[variant][':disabled'],
        // Since disabled styles can't effectively override.
        ':active': {},
        ':focus': {},
      },
    ],
  });

  const onChange =  async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { target: { files } } = e;

    try {
      setIsLoading(true);
      const result = files && await preparePayload(files[0], maxSize, types, asBlob, t, readFileDataURL);

      if (result) {
        await validator(result);

        if (isMounted.current) {
          // Only notify if onNewFile fails.
          try {
            // Use the latest onNewFile that is available.
            onNewFile(result);
          } catch (error) {
            notify(error, {
              name: 'File.onChange',
              context: {
                files: input.current && input.current.files,
                maxSize,
                types,
              },
            });

            // Propagate this to the next catch statement.
            throw error;
          }
        }
      }
    } catch (error) {
      setError(error);
    } finally {
      if (isMounted.current) {
        // Need to reset the value here otherwise the change event doesn't fire when uploading
        // the same file consecutively
        if (input.current) {
          input.current.value = '';
        }
        setIsLoading(false);
      }
    }
  };

  const setError = (error: Error) => {
    if (isMounted.current) {
      if (input.current) {
        input.current.value = '';
      }
      onError(error);
      setCurrentError(formatGenericError(error));
    }
  };

  const dismissError = () => {
    setCurrentError(null);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    // Redirect label keypress to file input.
    if ((e.nativeEvent.keyCode === SPACE_KEYCODE || e.nativeEvent.keyCode === ENTER_KEYCODE)
        && input.current) {
      e.preventDefault();
      input.current.click();
    }
  };

  /**
   * iOS Safari seems to have a problem that PDF mime type can only be
   * specified alone, it doesn't work if appears with other mime types on
   * the list.
   *
   * https://openradar.appspot.com/36726477
   * https://github.com/react-dropzone/react-dropzone/issues/627
   */
  const accept = userAgent().os !== OperatingSystem.iOS ? typeList(types) : undefined;

  return (
    <label
      aria-describedby={ariaDescribedBy}
      className={classes.label}
      style={{ outline: showOutline ? undefined : 'none' }}
      ref={focusableRef}
      role="button"
      onKeyPress={handleKeyPress}
    >
      <span className={isLoading ? classes.transparentChildren : undefined}>
        {children}
      </span>
      { isLoading &&
        <div className={classes.spinner}>
          <Spinner color="#333" />
        </div>}

      <div className={classes.inputWrap}>
        <input
          type="file"
          disabled={isDisabledOrLoading}
          accept={accept}
          ref={input}
          tabIndex={-1}
          onChange={!isDisabledOrLoading ? onChange : undefined}
          data-testid="fileInput"
        />
      </div>

      {showErrorModal && currentError &&
        <Message onClose={dismissError} variant={AlertVariant.Danger}>{currentError}</Message>}
    </label>
  );
};

function typeList(types: List<MediaType>) {
  return types.map(({ mimes }) => mimes).flatten(true).join(',');
}

const variantToStyles: {[variant in FileButtonVariantSubset]: IStyles} = {
  [LINK]: getLinkStyles(),
  [BUTTON]: BUTTON_STYLE,
  [TINY]: TINY_BUTTON_STYLE,
  [ICON]: ICON_BUTTON_STYLE,
  [MODAL_FOOTER]: MODAL_FOOTER_STYLE,
};

export function checkPayload(
  file: File,
  maxSize: number,
  types: List<MediaType>,
  t: tFunctionType,
) {
  const mediaType = createMimeFinder(types)(file.type);

  if (mediaType === undefined) {
    throw new Error(t('forms.errors.file-input.wrong-type', { type: file.type }));
  }

  if (file.size > maxSize) {
    throw new Error(
      t('forms.errors.file-input.too-big', { size: filesize(maxSize).human('si') }));
  }

  return file;
}

type EncodedString = { type: string, data: string };
type PreparePayloadReturnValue = EncodedString | File;

export async function preparePayload(
  file: File,
  maxSize: number,
  types: List<MediaType>,
  asBlob: boolean,
  t: tFunctionType,
  readFileDataURL: (file: Blob | File) => Promise<string>,
): Promise<PreparePayloadReturnValue> {
  checkPayload(file, maxSize, types, t);
  const mediaType = createMimeFinder(types)(file.type);
  try {
    if (!asBlob) {
      return file;
    }

    if (mediaType?.converter) {
      const blob = await mediaType?.converter(file);
      const data = await readFileDataURL(blob);
      return { type: blob.type, data };
    }

    return { type: file.type, data: await readFileDataURL(file) };
  } catch (error) {
    notify(error, {
      name: 'File.preparePayload',
      context: { file, maxSize },
    });

    throw error;
  }
}

export default FileInput;
